import '@/styles/globals.scss';
import 'react-responsive-modal/styles.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'react-datepicker/src/stylesheets/datepicker.scss';
import '@/styles/datepicker.scss';
import App, { AppContext, AppInitialProps, AppProps } from 'next/app';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useEffect, useState } from 'react';
import axiosInstance from '@/actions/axiosConfig';
import { GET_ENV_VARIABLES } from '@/configs/apiEndPoints';
import { AxiosResponse } from 'axios';
import { getEnvValue, storeEnvToLocalStorage } from '@/utils/envUtils';
import FrontendTracer from '@/otel/browser-instrumentation';
import { FeaturesType, fetchFeatures } from '@/actions/fetchFeatures';
import is from '@/utils/is';
import Fonts from '@/components/Fonts';
import Layout from '@/components/Layouts/Layout';
import { fetchAppConfig } from '@/actions/fetchAppConfig';
import Providers from '@/context/Providers';
import { CustomAppProps } from '@/types';
import { getProfileV2 } from '@/actions/fetchProfile';
import { CLIENT_COOKOIE, SESSION_COOKIE } from '@/configs/serviceConstants';
import Cookies from '@/utils/cookie';
import { getFooterDetail } from '@/actions/ecom/footer';
import { getHeaderDetail } from '@/actions/ecom/header';
import isAuthenticatedRoute from '@/utils/isAuthenticatedRoute';
import dynamic from 'next/dynamic';
import { usePageViewed } from '@/hooks/useEcomGTM';
import { useRouter } from 'next/router';
import useOnMount from '@/hooks/useOnMount';

const RouteProgressbar = dynamic(
  () => import(/* webpackChunkName: "app-lazy-ssr-chunks" */ '@/components/RouteProgressbar'),
  {
    ssr: false,
  },
);

/* istanbul ignore next */
if (is.client()) {
  if (getEnvValue('OTEL_SDK_DISABLED') === 'false') {
    console.log('UI TRACING ENABLED');
    /* istanbul ignore next */
    FrontendTracer();
  }
}

const CustomApp = ({
  Component,
  pageProps,
  router,
  config,
  profile,
  host,
}: CustomAppProps & AppProps) => {
  const [featureFlags, setFeatureFlags] = useState<FeaturesType>({});
  const { pathname } = useRouter();

  useEffect(() => {
    const fetchEnv = async () => {
      const response: AxiosResponse<{ [key: string]: string }> = await axiosInstance.get(
        GET_ENV_VARIABLES,
      );
      storeEnvToLocalStorage(response.data);
    };
    fetchEnv();
  }, []);

  const getFeatures = async () => {
    try {
      const features = await fetchFeatures();
      setFeatureFlags(features);
    } catch (error) {
      setFeatureFlags({});
    }
  };

  usePageViewed(router && router.pathname ? router.pathname : '', config);

  useEffect(() => {
    if (isAuthenticatedRoute(pathname) && is.empty.object(featureFlags)) {
      getFeatures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <ErrorBoundary>
      <Fonts />
      <RouteProgressbar />
      <Providers
        profile={profile}
        config={config}
        pageProps={pageProps}
        featureFlags={featureFlags}
        Component={Component}
        router={router}
      >
        <Layout pageProps={pageProps} Component={Component} router={router} host={host} />
      </Providers>
    </ErrorBoundary>
  );
};

CustomApp.getInitialProps = async (
  context: AppContext,
): Promise<CustomAppProps & AppInitialProps> => {
  const ctx = await App.getInitialProps(context);
  let promises = [fetchAppConfig(), getHeaderDetail(), getFooterDetail()];
  if (Cookies.has(SESSION_COOKIE, context.ctx.req?.headers?.cookie)) {
    promises.push(getProfileV2(context.ctx.req?.headers.cookie));
  } else {
    promises.push(Promise.resolve(null));
  }
  let config: any = {};
  let profile: any = {};

  try {
    const [appConfig, header, footer, userProfile] = await Promise.allSettled(promises);
    if (userProfile?.status === 'fulfilled') profile = userProfile.value;
    if (appConfig.status === 'fulfilled') config = appConfig.value;
    if (footer.status === 'fulfilled') config.footer = footer.value;
    if (header.status === 'fulfilled') config.header = header.value;
  } catch (error) {
    console.error(error);
    config = {};
  }

  if (Cookies.get(CLIENT_COOKOIE, context.ctx.req?.headers?.cookie) === 'flutter') {
    config['isFlutterClient'] = true;
  }
  return { ...ctx, config, profile, host: context.ctx.req?.headers.host };
};

export default CustomApp;
